import React, {
  useState,
  MouseEvent,
  useMemo,
  useCallback,
  ComponentProps,
} from 'react';

import styled from 'styled-components';

import { track } from '@adsk/acs-web-analytics';
import { EntitlementsProject, PRODUCT_IDS } from '@adsk/alloy-provider';
import { Box, Flex, Text } from '@adsk/alloy-react-box';
import { BasicButton } from '@adsk/alloy-react-button';
import ContentLoader from '@adsk/alloy-react-content-loader';
import { TriangleDownIcon, TriangleUpIcon } from '@adsk/alloy-react-icon';
import Menu, { MenuCategoryType, MenuItem } from '@adsk/alloy-react-menu';
import Overlay from '@adsk/alloy-react-overlay';
import theme from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { AnalyticsEvents } from '../../../constants';
import isOpenInNewTabMouseEvent from '../../../helpers/isOpenInNewTabMouseEvent';
import {
  getProductAndToolKey,
  parseProductAndToolKey,
} from '../../../utils/defaultProductAndToolHelpers';
import { getSelectedProduct } from '../../../utils/getSelectedProduct';
import { getSelectedTool } from '../../../utils/getSelectedTool';
import { useProductEntryFlowContext } from '../../Contexts/ProductEntryFlowContext';

type Product = EntitlementsProject['products'][0];
type Tool = Product['tools'][0];

const QuickAccessLoaderItem = () => (
  <ContentLoader width={100} height={20} viewBox="0 0 100 20">
    <rect x="0" y="0" rx="10" ry="10" width="100" height="20" />
  </ContentLoader>
);

const QuickAccessProductsButton = styled(BasicButton)({
  padding: '0 20px 0 0',
  width: '100%',
  justifyContent: 'start',
  color: theme.colors.charcoal900,
  '&:hover': {
    color: theme.colors.charcoal700,
  },
  [theme.mediaQuery.small]: {
    width: 'auto',
  },
});

type ProductsAndToolsSelectionMenuProps = {
  loading?: boolean;
  project: EntitlementsProject;
  selectedProductAndToolId?: string;
  currentToolId?: string;
  onProductClick?: (event: MouseEvent, product: Product) => void;
};

const ProductOrToolIcon = ({
  iconUrl,
  highlighted,
}: {
  iconUrl?: string | null;
  highlighted: boolean;
}) =>
  iconUrl ? (
    <span
      style={{
        maskImage: `url(${iconUrl})`,
        WebkitMaskImage: `url(${iconUrl})`,
        backgroundColor: highlighted
          ? theme.colors.white
          : theme.colors.charcoal900,
        width: 24,
        height: 24,
      }}
    />
  ) : null;

export const ProductsAndToolsSelectionMenu = ({
  project,
}: ProductsAndToolsSelectionMenuProps) => {
  const { loading, selectedProductAndToolIds, storeProductSelection } =
    useProductEntryFlowContext();
  const selectedProductAndToolId = selectedProductAndToolIds?.[project.id];
  const [menuOpen, setMenuOpen] = useState(false);

  const TriangleIconComponent = useMemo(
    () => (menuOpen ? TriangleUpIcon : TriangleDownIcon),
    [menuOpen],
  );

  const { productId: selectedProductId } = useMemo<{
    productId?: string;
  }>(
    () =>
      selectedProductAndToolId
        ? parseProductAndToolKey(selectedProductAndToolId)
        : {},
    [selectedProductAndToolId],
  );
  const selectedProduct = useMemo(
    () => getSelectedProduct(project, selectedProductId),
    [project, selectedProductId],
  );

  const selectedTool = useMemo(
    () => getSelectedTool(project, selectedProduct, selectedProductAndToolId),
    [project, selectedProduct, selectedProductAndToolId],
  );

  const updateDefaultAccess = useCallback(
    (
      event,
      {
        productAndToolKey,
        product,
        tool,
      }: { productAndToolKey: string; product: Product; tool?: Tool },
    ) => {
      event.preventDefault();
      track(AnalyticsEvents.productEntryFlowNavigate, {
        project_name: project.name,
        product_id: product.key,
        ...(tool && { tool_id: tool.key }),
      });
      storeProductSelection({
        project,
        productId: productAndToolKey,
        productName: product.name,
        ...(tool && { toolName: tool.name }),
      });
    },
    [project, storeProductSelection],
  );

  const options = useMemo(() => {
    return project.products.reduce((acc, product) => {
      const productCategory = product.category;
      let categoryObj = acc.find((cat) => cat.key === productCategory);
      if (!categoryObj) {
        categoryObj = {
          showSelection: false,
          key: productCategory,
          itemOptions: [],
        };
        acc.push(categoryObj);
      }

      const productItem = {
        id: product.key,
        name: product.name,
        key: product.key,
        label: <Box marginLeft={8}>{product.name}</Box>,
        highlighted: product.key === selectedProductId,
        showSelection: false,
        renderIcon: product.iconUrl
          ? () => (
              <ProductOrToolIcon
                iconUrl={product.iconUrl}
                highlighted={product.key === selectedProductId}
              />
            )
          : undefined,
        nested:
          product.tools &&
          product.tools.length &&
          product.key !== PRODUCT_IDS.ACCOUNT_ADMINISTRATION // WPX-2397
            ? [
                {
                  showSelection: false,
                  key: `${product.key}__category`,
                  itemOptions: product.tools.map((tool) => {
                    const productAndToolKey = getProductAndToolKey(
                      product.key,
                      tool.key,
                    );
                    const highlighted =
                      productAndToolKey === selectedProductAndToolId;
                    return {
                      as: 'a',
                      href: tool.url,
                      key: productAndToolKey,
                      id: productAndToolKey,
                      name: tool.name,
                      label: <Box marginLeft={8}>{tool.name}</Box>,
                      highlighted,
                      renderIcon: tool.iconUrl
                        ? () => (
                            <ProductOrToolIcon
                              iconUrl={tool.iconUrl}
                              highlighted={highlighted}
                            />
                          )
                        : undefined,
                      onClick: (e: React.MouseEvent<HTMLElement>) => {
                        if (isOpenInNewTabMouseEvent(e)) {
                          e.preventDefault();
                          track(AnalyticsEvents.quickAccessOpenInTab, {
                            project_name: project.name,
                            product_id: product.key,
                            tool_id: tool.key,
                          });
                          window.open(tool.url, '_blank');
                        } else {
                          updateDefaultAccess(e, {
                            tool,
                            productAndToolKey,
                            product,
                          });
                        }
                      },
                    };
                  }),
                },
              ]
            : undefined,
      };
      categoryObj.itemOptions.push(productItem);
      return acc;
    }, [] as MenuCategoryType<string>[]);
  }, [
    project.products,
    project.name,
    selectedProductId,
    selectedProductAndToolId,
    updateDefaultAccess,
  ]);

  return (
    <Overlay
      show={menuOpen}
      placement={Overlay.PLACEMENTS.BOTTOM_START}
      onHide={() => setMenuOpen(false)}
      renderTarget={({ targetProps }) => {
        return (
          <QuickAccessProductsButton
            disabled={loading}
            onClick={() => {
              setMenuOpen(!menuOpen);
              if (!menuOpen) {
                track(AnalyticsEvents.productEntryFlowOpen, {
                  project_name: project.name,
                });
              }
            }}
            {...targetProps}
          >
            {loading ? (
              <QuickAccessLoaderItem />
            ) : (
              <Flex justifyContent="left" alignItems="center" pr={3}>
                <Box
                  display="inline-flex"
                  borderRadius={4}
                  width={32}
                  minWidth={32}
                  height={32}
                  minHeight={32}
                  bg="adskBlue100"
                  alignItems="center"
                  justifyContent="center"
                >
                  {selectedProduct.iconUrl && (
                    <span
                      style={{
                        maskImage: `url(${selectedProduct.iconUrl})`,
                        WebkitMaskImage: `url(${selectedProduct.iconUrl})`,
                        backgroundColor: theme.colors.adskBlue500,
                        width: 24,
                        height: 24,
                      }}
                    />
                  )}
                </Box>

                <Flex flexDirection="column" textAlign="left">
                  <OverflowTooltip
                    triggerTarget={OverflowTooltip.TRIGGER_TARGETS.TOOLTIP}
                    content={selectedProduct.name}
                  >
                    <Text typography="bodyMedium" pl={8}>
                      {selectedProduct.name}
                    </Text>
                  </OverflowTooltip>
                  {selectedTool && (
                    <OverflowTooltip
                      triggerTarget={OverflowTooltip.TRIGGER_TARGETS.TOOLTIP}
                      content={selectedTool.name}
                    >
                      <Text typography="caption" pl={8}>
                        {selectedTool.name}
                      </Text>
                    </OverflowTooltip>
                  )}
                </Flex>
                <TriangleIconComponent
                  style={{
                    color: theme.colors.charcoal700,
                    margin: '0 4px',
                  }}
                  size={20}
                />
              </Flex>
            )}
          </QuickAccessProductsButton>
        );
      }}
      renderOverlay={({ overlayProps, placement, handleHide }) => (
        <Menu
          renderItem={(p) => {
            const product = project.products.find((prod) => prod.key === p.id);
            const extraProps = {} as Partial<ComponentProps<typeof MenuItem>>;
            if (product?.defaultUrl) {
              extraProps.as = 'a';
              extraProps.href = product.defaultUrl;
              extraProps.target = '_blank';
            }
            return (
              <MenuItem
                {...p}
                {...extraProps}
                onClick={async (event) => {
                  if (product) {
                    event.stopPropagation(); // Disable row click event
                    if (isOpenInNewTabMouseEvent(event)) {
                      track(AnalyticsEvents.quickAccessOpenInTab, {
                        project_name: project.name,
                        product_id: product.key,
                      });
                    } else {
                      updateDefaultAccess(event, {
                        productAndToolKey: product.key,
                        product,
                      });
                    }
                  }
                  handleHide();
                }}
              />
            );
          }}
          options={options}
          placement={placement}
          onClose={handleHide}
          ref={overlayProps.ref}
          style={overlayProps.style}
        />
      )}
    />
  );
};
